// src/lib/telemetry.ts
// ─────────────────────────────────────────────────────────────────────────────
// TELEMETRÍA DE VENTAS NIIZ — Analytics anónimo para el dueño
// de la plataforma (monetización de datos de mercado).
// ─────────────────────────────────────────────────────────────────────────────
// Actualización v110 (Octubre 2026):
//   · Envío CADA MINUTO (antes semanal): la telemetría anónima se sube tan
//     pronto hay datos pendientes y conexión, respetando el gobernador de
//     tráfico para no saturar la API.
//   · Cero `any` — tipado estricto en hooks de Dexie y cliente Supabase.
//   · Integración con `trafficGovernor` (tier FREE, trigger background).
//   · Hash FNV-1a determinista (igual que `lineage.ts`) reemplaza DJB2.
//   · Métricas de envío: conteo, bytes, latencia, último éxito.
//   · Soporte multi-sede (branch_id) y self-test integrado.
//   · 100% aditivo: preserva captura automática y privacidad zero-PII.
//
// Privacidad:
//   - Recolección 100% LOCAL (offline-first): cada venta COMPLETADA se
//     normaliza y guarda en IndexedDB (tabla `telemetry_events`).
//   - Categorización: producto, categoría, día de semana, mes, hora,
//     fin de semana, tipo de pedido y método de pago.
//   - PRIVACIDAD: NO se recolectan datos personales de clientes (sin nombres,
//     teléfonos, direcciones). business_id se hashea con FNV-1a irreversible.
//   - Envío: cada minuto (si hay datos + conexión + ventana de tráfico).
// ─────────────────────────────────────────────────────────────────────────────

import {
  db,
  type Sale,
  type SaleItem,
  type TelemetryEvent,
  type TelemetrySaleItem,
  type AppSettings,
} from '@/db/db';
import { supabase } from '@/lib/supabase';
import { trafficGovernor } from '@/lib/trafficGovernor';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🆕 Intervalo de revisión: CADA MINUTO (antes 6 horas).
 * Si hay datos pendientes y conexión, se intenta el envío.
 */
export const CHECK_INTERVAL_MS = 60_000;

/**
 * 🆕 Cooldown mínimo entre envíos exitosos para no martillar la API.
 * 60 segundos = 1 minuto (antes era WEEK_MS = 7 días).
 */
export const UPLOAD_COOLDOWN_MS = 60_000;

/** Tamaño máximo del lote por envío. */
export const BATCH_LIMIT = 500;

/** Delay inicial tras arrancar la app antes del primer check (ms). */
export const INITIAL_CHECK_DELAY_MS = 15_000;

/** Retardo del flush post-transacción para evitar PrematureCommitError. */
const FLUSH_DEBOUNCE_MS = 300;

// ─────────────────────────────────────────────────────────────────────────────
// ESTADO INTERNO
// ─────────────────────────────────────────────────────────────────────────────

let initialized = false;
const pendingSaleIds = new Set<string>();
let flushScheduled = false;
/** Los errores de permisos (403/RLS) se avisan UNA sola vez. */
let warnedPermissionDenied = false;
/** Timestamp del último envío exitoso. */
let lastSuccessfulUploadMs = 0;

// ─────────────────────────────────────────────────────────────────────────────
// MÉTRICAS DE ENVÍO (observabilidad)
// ─────────────────────────────────────────────────────────────────────────────

export interface TelemetryMetrics {
  /** Total de eventos enviados exitosamente desde el arranque. */
  totalUploaded: number;
  /** Bytes totales enviados desde el arranque. */
  totalBytes: number;
  /** Latencia promedio del último lote (ms). */
  lastLatencyMs: number | null;
  /** Timestamp ISO del último envío exitoso. */
  lastSuccessAt: string | null;
  /** Intentos fallidos desde el arranque. */
  failedAttempts: number;
  /** Eventos pendientes en cola. */
  pendingCount: number;
}

const metrics: TelemetryMetrics = {
  totalUploaded: 0,
  totalBytes: 0,
  lastLatencyMs: null,
  lastSuccessAt: null,
  failedAttempts: 0,
  pendingCount: 0,
};

/** Retorna una copia de las métricas actuales (inmutable). */
export const getTelemetryMetrics = (): Readonly<TelemetryMetrics> => ({ ...metrics });

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 🆕 Hash FNV-1a 32-bit determinista (idéntico al de `lineage.ts`).
 * Reemplaza al antiguo DJB2 para mantener consistencia criptográfica
 * en toda la plataforma. Irreversible en la práctica para IDs cortos.
 *
 * @example hashBusinessId('biz-abc123') // "7f3a9c21"
 */
export const hashBusinessId = (id: string): string => {
  let h = 0x811c9dc5; // FNV offset basis
  const s = String(id || 'unknown');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193); // FNV prime
  }
  return (h >>> 0).toString(16).padStart(8, '0');
};

/** ¿El error es de PERMISOS (RLS/403)? En ese caso no se ensucia la consola. */
const isPermissionError = (error: unknown): boolean => {
  const err: Record<string, unknown> =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const code = String(err['code'] ?? '');
  const status = Number(err['status'] ?? err['statusCode']);
  const message = String(err['message'] ?? '').toLowerCase();
  return (
    code === '42501' ||
    status === 401 ||
    status === 403 ||
    message.includes('row-level security') ||
    message.includes('permission denied') ||
    message.includes('violates row-level security') ||
    message.includes('not allowed')
  );
};

/** Aviso ÚNICO (console.info, nunca error) para problemas de permisos. */
const warnPermissionOnce = (detail: string): void => {
  if (warnedPermissionDenied) return;
  warnedPermissionDenied = true;
  console.info(
    `📊 Telemetría: el servidor rechazó el envío por permisos (${detail}). ` +
    'La telemetría se queda en local y se reintentará cuando la nube esté disponible.',
  );
};

/** Cliente compartido de Supabase (sin crear instancias duplicadas de GoTrue). */
const getClient = () => supabase;

/** Verifica si la telemetría está habilitada en settings (por defecto SÍ). */
export const isTelemetryEnabled = async (): Promise<boolean> => {
  try {
    const settings = await db.settings.toCollection().first();
    // Si el campo no existe, está habilitada por defecto (compatibilidad).
    return settings?.telemetry_enabled !== false;
  } catch {
    return true;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 1. NORMALIZACIÓN DE UNA VENTA → EVENTO DE TELEMETRÍA
// ─────────────────────────────────────────────────────────────────────────────

const buildTelemetryEvent = async (sale: Sale): Promise<TelemetryEvent | null> => {
  if (!sale.id) return null;

  // Productos para enriquecer categoría/tipo (si el producto aún existe)
  const items: SaleItem[] = await db.sale_items.where('sale_id').equals(sale.id).toArray();
  if (items.length === 0) return null;

  const productIds = items.map(i => i.product_id).filter(Boolean) as string[];
  const products = productIds.length > 0
    ? await db.products.where('id').anyOf(productIds).toArray()
    : [];
  const productMap = new Map(products.map(p => [p.id ?? '', p]));

  // Tipo de negocio (para segmentar el mercado en el servidor)
  const settings = await db.settings.where('business_id').equals(sale.business_id).first();
  const businessType = (settings as AppSettings | undefined)?.business_type;

  const occurredAt = sale.created_at ? new Date(sale.created_at) : new Date();

  const telemetryItems: TelemetrySaleItem[] = items.map(item => {
    const product = item.product_id ? productMap.get(item.product_id) : undefined;
    return {
      product_name: item.product_name || 'Producto',
      category: product?.category_id ? `cat-${product.category_id}` : 'General',
      product_type: product?.type || 'FISICO',
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      line_total: item.total_price || ((item.unit_price || 0) * (item.quantity || 0)),
    };
  });

  const event: TelemetryEvent = {
    id: crypto.randomUUID(),
    business_hash: hashBusinessId(sale.business_id),
    sale_id: sale.id,
    occurred_at: occurredAt.toISOString(),
    day_of_week: occurredAt.getDay(),
    month: occurredAt.getMonth() + 1,
    hour: occurredAt.getHours(),
    is_weekend: [0, 6].includes(occurredAt.getDay()),
    total_amount: sale.total || 0,
    item_count: items.length,
    items: telemetryItems,
    uploaded: 0,
    created_at: new Date().toISOString(),
  };
  if (businessType) event.business_type = businessType;
  if (sale.order_type) event.order_type = sale.order_type;
  if (sale.payment_method) event.payment_method = sale.payment_method;

  return event;
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. CAPTURA AUTOMÁTICA (Hooks de Dexie sobre ventas)
// ─────────────────────────────────────────────────────────────────────────────

const queueSale = (saleId: string): void => {
  if (!saleId) return;
  pendingSaleIds.add(saleId);
  if (!flushScheduled) {
    flushScheduled = true;
    // Esperamos a que la transacción original termine antes de leer
    setTimeout(() => {
      flushScheduled = false;
      void processQueue();
    }, FLUSH_DEBOUNCE_MS);
  }
};

const processQueue = async (): Promise<void> => {
  // Respetar configuración del negocio
  if (!(await isTelemetryEnabled())) return;

  const ids = [...pendingSaleIds];
  pendingSaleIds.clear();

  for (const saleId of ids) {
    try {
      // Dedupe: si ya existe un evento para esta venta, la saltamos
      const exists = await db.telemetry_events.where('sale_id').equals(saleId).first();
      if (exists) continue;

      const sale = await db.sales.get(saleId);
      if (!sale || sale.status !== 'COMPLETED') continue;

      const event = await buildTelemetryEvent(sale);
      if (event) await db.telemetry_events.add(event);
    } catch (err) {
      console.warn('📊 Telemetría: no se pudo registrar la venta', saleId, err);
    }
  }
};

/** Registra manualmente una venta ya existente (útil para backfill/testing). */
export const recordSaleTelemetry = async (saleId: string): Promise<void> => {
  queueSale(saleId);
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. ENVÍO PERIÓDICO A SUPABASE (cada minuto con cooldown)
// ─────────────────────────────────────────────────────────────────────────────

export const getPendingCount = async (): Promise<number> => {
  try {
    const count = await db.telemetry_events.where('uploaded').equals(0).count();
    metrics.pendingCount = count;
    return count;
  } catch {
    return 0;
  }
};

export const getLastUpload = async (): Promise<string | null> => {
  try {
    const settings = await db.settings.orderBy('created_at').last();
    return settings?.telemetry_last_upload || null;
  } catch {
    return null;
  }
};

/**
 * 🆕 ¿Ya pasó el cooldown desde el último envío?
 * Antes comparaba con WEEK_MS (7 días), ahora con UPLOAD_COOLDOWN_MS (1 min).
 */
export const isUploadDue = async (): Promise<boolean> => {
  // 1) Cooldown en memoria (más rápido y preciso)
  if (lastSuccessfulUploadMs > 0) {
    if (Date.now() - lastSuccessfulUploadMs < UPLOAD_COOLDOWN_MS) return false;
  }
  // 2) Fallback: última fecha persistida en settings
  const last = await getLastUpload();
  if (!last) return true;
  const lastMs = new Date(last).getTime();
  return Date.now() - lastMs >= UPLOAD_COOLDOWN_MS;
};

/**
 * Sube el lote pendiente. Retorna cuántos registros se enviaron.
 * Respeta el gobernador de tráfico y el cooldown de 1 minuto.
 */
export const uploadPendingTelemetry = async (): Promise<{
  uploaded: number;
  error?: string;
  latencyMs?: number;
}> => {
  const client = getClient();
  if (!client) {
    return { uploaded: 0, error: 'Supabase no configurado (revisa VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY)' };
  }
  if (!navigator.onLine) return { uploaded: 0, error: 'Sin conexión a internet' };
  if (!(await isTelemetryEnabled())) return { uploaded: 0, error: 'Telemetría deshabilitada en configuración' };

  // 🆕 Respetar el gobernador de tráfico (tier FREE: solo sube si servidor normal/libre)
  const decision = trafficGovernor.evaluate({
    tier: 'FREE',
    trigger: 'background',
    isOnline: navigator.onLine,
  });
  if (decision.action === 'DEFER') {
    return { uploaded: 0, error: `Aplazado: ${decision.reason}` };
  }
  if (decision.delayMs > 0) {
    await new Promise(resolve => setTimeout(resolve, decision.delayMs));
  }

  try {
    const pending = await db.telemetry_events
      .where('uploaded')
      .equals(0)
      .limit(BATCH_LIMIT)
      .toArray();

    if (pending.length === 0) return { uploaded: 0 };

    // Quitamos campos internos antes de subir
    const payload = pending.map(({ uploaded: _uploaded, ...rest }) => rest);
    const payloadBytes = new Blob([JSON.stringify(payload)]).size;

    const startTime = Date.now();
    // @ts-expect-error — Supabase tipado genérico, tabla existe en schema
    const { error } = await client.from('telemetry_events').insert(payload);
    const latencyMs = Date.now() - startTime;
    trafficGovernor.recordLatency(latencyMs);

    if (error) {
      metrics.failedAttempts += 1;
      trafficGovernor.recordFailure(error);
      if (isPermissionError(error)) {
        warnPermissionOnce(String(error.message));
        return { uploaded: 0, error: 'Sin permisos de telemetría (RLS)', latencyMs };
      }
      console.warn('📊 Telemetría: no se pudo subir el lote:', error.message);
      return { uploaded: 0, error: error.message, latencyMs };
    }

    // Marcar como enviados (solo los que subimos en este lote)
    const uploadedIds = pending.map(e => e.id);
    await db.telemetry_events.where('id').anyOf(uploadedIds).modify({ uploaded: 1 });

    // Registrar fecha de envío en settings
    const nowIso = new Date().toISOString();
    const settings = await db.settings.orderBy('created_at').last();
    if (settings?.id) {
      await db.settings.update(settings.id, { telemetry_last_upload: nowIso });
    }

    // Actualizar métricas
    metrics.totalUploaded += pending.length;
    metrics.totalBytes += payloadBytes;
    metrics.lastLatencyMs = latencyMs;
    metrics.lastSuccessAt = nowIso;
    lastSuccessfulUploadMs = Date.now();
    metrics.pendingCount = Math.max(0, metrics.pendingCount - pending.length);
    trafficGovernor.recordBatchSuccess();

    return { uploaded: pending.length, latencyMs };
  } catch (err: unknown) {
    metrics.failedAttempts += 1;
    trafficGovernor.recordFailure(err);
    if (isPermissionError(err)) {
      const record: Record<string, unknown> =
        err && typeof err === 'object' ? (err as Record<string, unknown>) : {};
      warnPermissionOnce(String(record['message'] ?? '') || '403');
      return { uploaded: 0, error: 'Sin permisos de telemetría (RLS)' };
    }
    const msg = err instanceof Error ? err.message : 'Error desconocido';
    console.warn('📊 Telemetría: fallo en el envío programado:', msg);
    return { uploaded: 0, error: msg };
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. INICIALIZACIÓN (hooks + scheduler cada minuto)
// ─────────────────────────────────────────────────────────────────────────────

const registerHooks = (): void => {
  // Ventas creadas ya COMPLETADAS (ej: POS retail vía NiizManager)
  db.sales.hook('creating', (primKey, obj) => {
    const sale = obj as Partial<Sale>;
    if (sale?.status === 'COMPLETED') {
      queueSale(String(sale.id || primKey));
    }
  });

  // Ventas que se marcan COMPLETED al cobrar (ej: restaurante)
  db.sales.hook('updating', (modifications, primKey) => {
    const mods = modifications as Partial<Sale>;
    if (mods?.status === 'COMPLETED') {
      queueSale(String(primKey));
    }
  });
};

const checkUpload = async (): Promise<void> => {
  try {
    if (!navigator.onLine) return;
    if (!(await isTelemetryEnabled())) return;

    const pending = await getPendingCount();
    if (pending === 0) return;

    // 🆕 Ahora el envío es CADA MINUTO (cooldown de 60s), no semanal.
    if (await isUploadDue()) {
      const res = await uploadPendingTelemetry();
      if (res.uploaded > 0) {
        console.log(
          `📊 Telemetría: ${res.uploaded} eventos enviados ` +
          `(${res.latencyMs ?? '?'}ms · ${metrics.totalUploaded} acumulados)`,
        );
      }
    }
  } catch (err) {
    console.warn('📊 Telemetría: revisión periódica fallida', err);
  }
};

/** Llama esto UNA vez al arrancar la app (desde main.tsx). */
export const initTelemetry = (): void => {
  if (initialized) return;
  initialized = true;

  registerHooks();

  // 🆕 Revisión inicial (tras 15s de arrancar) y periódica CADA MINUTO
  setTimeout(() => void checkUpload(), INITIAL_CHECK_DELAY_MS);
  setInterval(() => void checkUpload(), CHECK_INTERVAL_MS);

  // Al volver la conexión, intentamos el envío pendiente
  window.addEventListener('online', () => void checkUpload());

  // Intento final antes de cerrar/ocultar la pestaña
  window.addEventListener('pagehide', () => {
    void processQueue();
  });

  console.log(
    `📊 Telemetría inicializada: check cada ${CHECK_INTERVAL_MS / 1000}s · ` +
    `cooldown ${UPLOAD_COOLDOWN_MS / 1000}s · lote máx ${BATCH_LIMIT}`,
  );
};

/**
 * 🆕 Fuerza un envío inmediato (útil para testing o botón manual).
 * Ignora el cooldown pero respeta el gobernador de tráfico.
 */
export const forceUploadNow = async (): Promise<{
  uploaded: number;
  error?: string;
}> => {
  lastSuccessfulUploadMs = 0; // resetear cooldown
  return uploadPendingTelemetry();
};

/**
 * 🆕 Resetea las métricas de envío (útil para tests o cambio de sesión).
 */
export const resetTelemetryMetrics = (): void => {
  metrics.totalUploaded = 0;
  metrics.totalBytes = 0;
  metrics.lastLatencyMs = null;
  metrics.lastSuccessAt = null;
  metrics.failedAttempts = 0;
  metrics.pendingCount = 0;
  lastSuccessfulUploadMs = 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. SELF-TEST INTEGRADO
// ─────────────────────────────────────────────────────────────────────────────

export function runTelemetrySelfTest(): number {
  let passed = 0;
  const assert = (cond: boolean, label: string): void => {
    if (!cond) throw new Error(`[telemetry SELF-TEST FAIL] ${label}`);
    passed++;
  };

  // ── Hash FNV-1a determinista ──
  const h1 = hashBusinessId('biz-abc123');
  const h2 = hashBusinessId('biz-abc123');
  const h3 = hashBusinessId('biz-xyz789');
  assert(h1 === h2, 'Hash determinista: mismo input = mismo output');
  assert(h1 !== h3, 'Hash distinto para inputs distintos');
  assert(h1.length === 8, 'Hash siempre 8 caracteres hex');
  assert(/^[0-9a-f]{8}$/.test(h1), 'Hash formato hex válido');
  assert(hashBusinessId('') === hashBusinessId('unknown'), 'Hash vacío = unknown');

  // ── Detección de errores de permisos ──
  assert(isPermissionError({ code: '42501' }), 'Detecta code 42501 (RLS)');
  assert(isPermissionError({ status: 403 }), 'Detecta status 403');
  assert(isPermissionError({ status: 401 }), 'Detecta status 401');
  assert(isPermissionError({ message: 'row-level security policy' }), 'Detecta RLS por mensaje');
  assert(isPermissionError({ message: 'permission denied for table' }), 'Detecta permission denied');
  assert(!isPermissionError({ status: 500 }), 'No detecta 500 como permisos');
  assert(!isPermissionError({ message: 'not found' }), 'No detecta not found como permisos');
  assert(!isPermissionError(null), 'No rompe con null');
  assert(!isPermissionError(undefined), 'No rompe con undefined');

  // ── Constantes correctas ──
  assert(CHECK_INTERVAL_MS === 60_000, 'CHECK_INTERVAL_MS = 60s (cada minuto)');
  assert(UPLOAD_COOLDOWN_MS === 60_000, 'UPLOAD_COOLDOWN_MS = 60s');
  assert(BATCH_LIMIT === 500, 'BATCH_LIMIT = 500');
  assert(INITIAL_CHECK_DELAY_MS === 15_000, 'INITIAL_CHECK_DELAY_MS = 15s');

  // ── Métricas iniciales ──
  const m = getTelemetryMetrics();
  assert(m.totalUploaded === 0, 'Métricas inician en 0');
  assert(m.failedAttempts === 0, 'Sin fallos iniciales');
  assert(m.lastLatencyMs === null, 'Sin latencia inicial');

  // ── Reset de métricas ──
  resetTelemetryMetrics();
  const m2 = getTelemetryMetrics();
  assert(m2.totalUploaded === 0, 'Reset funciona');

  return passed;
}

// ─── Auto-ejecución en desarrollo ───
if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
  try {
    const count = runTelemetrySelfTest();
    console.log(`✅ [telemetry.ts] ${count} aserciones internas verificadas.`);
  } catch (err) {
    console.error('🔴 [telemetry.ts] FALLO en pruebas internas:', err);
  }
}